import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Section, StatBadge } from "@/components/Chrome";
import SetupCredentialsDialog from "@/components/SetupCredentialsDialog";
import ConsentDialog from "@/components/ConsentDialog";
import { api, API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Envelope, Calendar, ChatDots, MicrosoftOutlookLogo, Buildings, GoogleLogo, Star } from "@phosphor-icons/react";

const CARD_META = {
  google_bundle:   { label: "Google (Gmail + Calendar)", icon: GoogleLogo,           desc: "Read email + calendar in one grant. Recommended for most users.", popular: true, provider: "google" },
  slack:           { label: "Slack",                     icon: ChatDots,             desc: "Watch mentions, surface important threads.", popular: true, provider: "slack" },
  gmail:           { label: "Gmail only",                icon: Envelope,             desc: "Read messages, draft replies. Use Google bundle if you also want Calendar.", provider: "google" },
  google_calendar: { label: "Google Calendar only",      icon: Calendar,             desc: "Hold slots, brief you on the day. Use Google bundle for full coverage.", provider: "google" },
  outlook:         { label: "Outlook / M365",            icon: MicrosoftOutlookLogo, desc: "Email + calendar for Microsoft accounts.", provider: "microsoft" },
  hubspot:         { label: "HubSpot",                   icon: Buildings,            desc: "Auto-add new contacts, update deal stages.", provider: "hubspot" },
};

// Curated display order — bundle first, popular next, secondary providers grouped under "More".
const PRIMARY_ORDER = ["google_bundle", "slack", "outlook", "hubspot"];
const MORE_ORDER    = ["gmail", "google_calendar"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Integrations() {
  const qc = useQueryClient();
  const { data: dbRows = [] } = useQuery({ queryKey: ["integrations"], queryFn: api.integrations.list });
  const [params, setParams] = useSearchParams();
  const [wizard, setWizard]   = useState({ open: false, integration: null, provider: null });
  const [consent, setConsent] = useState({ open: false, integration: null });
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const connected = params.get("connected");
    const err = params.get("oauth_error");
    if (connected) {
      const label = CARD_META[connected]?.label || connected;
      toast.success(`Connected ${label} via OAuth`);
      qc.invalidateQueries({ queryKey: ["integrations"] });
      params.delete("connected"); setParams(params, { replace: true });
    }
    if (err) {
      toast.error(`OAuth failed: ${decodeURIComponent(err)}`);
      params.delete("oauth_error"); setParams(params, { replace: true });
    }
  }, [params, setParams, qc]);

  // Derive card data — bundle is synthetic; its state is derived from gmail + google_calendar rows.
  const rowByName = Object.fromEntries(dbRows.map(r => [r.name, r]));
  const googleConfigured = rowByName.gmail?.provider_configured || rowByName.google_calendar?.provider_configured || false;

  const cards = [];
  const push = (name) => {
    const meta = CARD_META[name];
    if (!meta) return;
    let row;
    if (name === "google_bundle") {
      const bothConn = rowByName.gmail?.status === "connected" && rowByName.google_calendar?.status === "connected";
      row = {
        name: "google_bundle", provider: "google",
        provider_configured: googleConfigured,
        status: bothConn ? "connected" : "disconnected",
        account_email: rowByName.gmail?.account_email || rowByName.google_calendar?.account_email || null,
        is_mocked: false,
      };
    } else {
      row = rowByName[name];
    }
    if (row) cards.push({ ...row, meta });
  };
  PRIMARY_ORDER.forEach(push);
  if (showMore) MORE_ORDER.forEach(push);

  const startOAuth = async (integration) => {
    try {
      const url = `${API}/oauth/${integration}/start`;
      const { data } = await axios.get(url, { withCredentials: true });
      if (data?.authorize_url) window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(detail, { duration: 8000 });
    }
  };

  const onConnect = (row) => {
    if (!row.provider_configured) {
      setWizard({ open: true, integration: row.name, provider: row.provider });
    } else {
      setConsent({ open: true, integration: row.name });
    }
  };

  const disconnect = useMutation({
    mutationFn: async (name) => {
      // Bundle disconnect = disconnect both underlying integrations.
      if (name === "google_bundle") {
        await api.integrations.disconnect("gmail");
        await api.integrations.disconnect("google_calendar");
      } else {
        await api.integrations.disconnect(name);
      }
    },
    onSuccess: (_, name) => {
      qc.invalidateQueries({ queryKey: ["integrations"] });
      toast(`Disconnected ${CARD_META[name]?.label || name}`);
    },
  });

  const retryConnect = () => {
    if (wizard.integration) {
      // After user says "I've added them", jump straight to consent.
      setConsent({ open: true, integration: wizard.integration });
    }
  };

  return (
    <div data-testid="integrations-page">
      <PageHeader
        eyebrow="Data sources"
        title="Connected accounts"
        subtitle="Copilot only reads what you allow, and asks you before doing anything high-stakes. Start with the most popular — you can add more anytime."
      />
      <Section title="Recommended for most users">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-0 -mx-10" data-testid="integrations-grid">
          {cards.map((row, idx) => <Card key={row.name} row={row} idx={idx} onConnect={onConnect} onDisconnect={() => disconnect.mutate(row.name)} />)}
        </ul>
        <div className="mt-6 flex justify-center">
          {!showMore ? (
            <button data-testid="btn-show-more"
              onClick={() => setShowMore(true)}
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 hover:border-stone-500 text-stone-300 transition-colors">
              Show more providers
            </button>
          ) : (
            <button data-testid="btn-hide-more"
              onClick={() => setShowMore(false)}
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 hover:border-stone-500 text-stone-300 transition-colors">
              Hide extra providers
            </button>
          )}
        </div>
      </Section>

      <SetupCredentialsDialog
        open={wizard.open}
        onOpenChange={(o) => setWizard(w => ({ ...w, open: o }))}
        integration={wizard.integration}
        provider={wizard.provider}
        backendUrl={BACKEND_URL}
        onRetry={retryConnect}
      />
      <ConsentDialog
        open={consent.open}
        onOpenChange={(o) => setConsent(c => ({ ...c, open: o }))}
        integration={consent.integration}
        onContinue={() => startOAuth(consent.integration)}
      />
    </div>
  );
}

function Card({ row, idx, onConnect, onDisconnect }) {
  const Icon = row.meta.icon;
  const isConn = row.status === "connected";
  const configured = row.provider_configured;

  return (
    <li
      data-testid={`integration-${row.name}`}
      className={`px-10 py-6 border-t border-stone-800 ${idx % 2 === 0 ? "md:border-r md:border-stone-800" : ""}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 bg-stone-900 border border-stone-800 flex items-center justify-center shrink-0">
          <Icon size={20} weight="duotone" className="text-stone-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-base text-stone-100">{row.meta.label}</h3>
            {row.meta.popular && (
              <span data-testid={`badge-popular-${row.name}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-orange-700 text-orange-400 text-[10px] font-mono uppercase tracking-widest">
                <Star size={10} weight="fill" /> Most popular
              </span>
            )}
            <StatBadge status={isConn ? "success" : "info"}>{isConn ? "Connected" : "Not connected"}</StatBadge>
            {!configured && !isConn && <StatBadge status="warning" data-testid={`badge-not-configured-${row.name}`}>Needs setup</StatBadge>}
            {isConn && !row.is_mocked && <StatBadge status="accent">Live OAuth</StatBadge>}
          </div>
          <p className="text-xs text-stone-500 mt-1.5">{row.meta.desc}</p>
          {isConn && row.account_email && (
            <div className="text-[11px] font-mono text-stone-500 mt-2">{row.account_email}</div>
          )}
          <div className="mt-4">
            {isConn ? (
              <button
                data-testid={`disconnect-${row.name}`}
                onClick={onDisconnect}
                className="text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 border border-stone-700 text-stone-400 hover:text-stone-100 hover:border-stone-500 transition-colors">
                Disconnect
              </button>
            ) : (
              <button
                data-testid={`connect-${row.name}`}
                onClick={() => onConnect(row)}
                className="text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white transition-colors">
                {configured ? "Connect" : "Set up"}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
